import { FeedType } from "../Enums/enums";
import { PARTNER_FEED_PLATFORMS } from "../Enums/platform_constants";

/**
 * Platforms per Partner feed (user request 2026-10-14) — an ADDITION that only
 * applies when a brand has two or more Partner feeds selected. With one
 * Partner feed, Social feeds, or one of each, nothing here is used and the
 * wizard works exactly as before.
 *
 * State: `partnerFeedPlatforms` = { brandName: { feedId: [platforms] } }. A
 * feed with no entry uses the brand's own Partner selection (everything the
 * brand has ticked), so nothing changes until a feed is unticked. The
 * brand's Partner selection (`brandPlatforms`) is kept as the UNION of its
 * feeds' lists, so every platform card below still works per platform.
 */

/** The brand's selected Partner feeds (objects from Step 1's feed info). */
export function selectedPartnerFeeds(brandName, selectedFeeds, brandFeedsInfo) {
  const ids = selectedFeeds?.[brandName] || [];
  return (brandFeedsInfo?.[brandName] || []).filter((f) => ids.includes(f.id) && f.feed_type === FeedType.PARTNER);
}

/** True when the brand has two or more Partner feeds selected — the only case the feature applies. */
export function usesPerFeedPartners(brandName, selectedFeeds, brandFeedsInfo) {
  return selectedPartnerFeeds(brandName, selectedFeeds, brandFeedsInfo).length >= 2;
}

/** The brand's selected Partner platforms. */
export const brandPartnerPlatforms = (brandPlatforms, brandName) =>
  (brandPlatforms?.[brandName] || []).filter((p) => PARTNER_FEED_PLATFORMS.includes(p));

/** One Partner feed's platforms: its own list, else the brand's Partner selection. */
export function feedPartnerPlatforms(brandName, feedId, brandPlatforms, partnerFeedPlatforms) {
  const own = partnerFeedPlatforms?.[brandName]?.[feedId];
  const brandList = brandPartnerPlatforms(brandPlatforms, brandName);
  // Only platforms the brand still has: removing one at brand level removes it everywhere.
  return Array.isArray(own) ? own.filter((p) => brandList.includes(p)) : brandList;
}

/**
 * The request field `partner_feed_platforms`: { brandName: { feedId: [platforms] } },
 * only for brands with two or more Partner feeds. Empty object otherwise, which
 * the server reads as "use the brand's platforms, as before".
 */
export function buildPartnerFeedPlatforms(selectedBrands, selectedFeeds, brandFeedsInfo, brandPlatforms, partnerFeedPlatforms) {
  const out = {};
  (selectedBrands || []).forEach(({ name }) => {
    if (!usesPerFeedPartners(name, selectedFeeds, brandFeedsInfo)) return;
    out[name] = {};
    selectedPartnerFeeds(name, selectedFeeds, brandFeedsInfo).forEach((f) => {
      out[name][f.id] = feedPartnerPlatforms(name, f.id, brandPlatforms, partnerFeedPlatforms);
    });
  });
  return out;
}

/** Partner feeds (with 2+ per brand) left with no platform ticked: [{ brandName, feed }]. */
export function partnerFeedsWithoutPlatforms(selectedBrands, selectedFeeds, brandFeedsInfo, brandPlatforms, partnerFeedPlatforms) {
  const out = [];
  (selectedBrands || []).forEach(({ name }) => {
    if (!usesPerFeedPartners(name, selectedFeeds, brandFeedsInfo)) return;
    selectedPartnerFeeds(name, selectedFeeds, brandFeedsInfo).forEach((f) => {
      if (feedPartnerPlatforms(name, f.id, brandPlatforms, partnerFeedPlatforms).length === 0) out.push({ brandName: name, feed: f });
    });
  });
  return out;
}

/** A feed's display name. */
export const feedLabel = (feed) => feed?.title || feed?.name || `Feed ${feed?.id}`;
